import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, 
    faTimes, 
    faVideo, 
    faImage, 
    faMicrophone, 
    faSpinner, 
    faExternalLinkAlt,
    faCheckCircle,
    faTimesCircle,
    faInfoCircle,
    faEdit,
    faFolderOpen
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { sendToMediaCentre, updateMediaCentreItem } from '../services/mediaService';
import { galleryService } from '../services/galleryService';
import GalleryBrowser from '../components/Galleries/GalleryBrowser';
import './ModalAddMediaCentre.css';

const ModalAddMediaCentre = ({ isOpen, onClose, onSuccess, choiceFields, editingItem = null, isEditMode = false }) => {
    const [formData, setFormData] = useState({
        webURLLink: '',
        mediaType: 'Video',
        topicName: '',
        shortDescription: '',
        series: [],
        productService: '',
        theme: '',
        entity: 'IF',
        thumbnail: '',
        briefingCode: '',
        published: true,
        publishDate: new Date().toISOString().split('T')[0] // Default to today
    });
    const isBriefing = formData.mediaType === 'Briefing';

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [urlPreview, setUrlPreview] = useState(null);

    // Gallery browser state
    const [showGalleryBrowser, setShowGalleryBrowser] = useState(false);
    const [galleries, setGalleries] = useState([]);
    const [loadingGalleries, setLoadingGalleries] = useState(false);
    const [galleryBrowseTarget, setGalleryBrowseTarget] = useState('media'); // 'media' or 'thumbnail'

    // Custom field add states
    const [isAddingNewTopic, setIsAddingNewTopic] = useState(false);
    const [customTopicName, setCustomTopicName] = useState('');
    const [isAddingNewProduct, setIsAddingNewProduct] = useState(false);
    const [customProductName, setCustomProductName] = useState('');
    const [isAddingNewTheme, setIsAddingNewTheme] = useState(false);
    const [customThemeName, setCustomThemeName] = useState('');
    const [isAddingNewEntity, setIsAddingNewEntity] = useState(false);
    const [customEntityName, setCustomEntityName] = useState('');

    // Load galleries when modal opens
    useEffect(() => {
        if (isOpen) {
            loadGalleries();
        }
    }, [isOpen]);

    const loadGalleries = async () => {
        setLoadingGalleries(true);
        try {
            const galleriesData = await galleryService.getGalleries();
            setGalleries(galleriesData || []);
        } catch (error) {
            console.error('Error loading galleries:', error);
            toast.error('Failed to load galleries');
        } finally {
            setLoadingGalleries(false);
        }
    };

    const handleMediaSelect = (selectedMedia) => {
      
        
        // Use the media name as the URL
        const mediaUrl = selectedMedia.name;
        
        if (galleryBrowseTarget === 'thumbnail') {
            // Selected for thumbnail
            setFormData(prev => ({ 
                ...prev, 
                thumbnail: mediaUrl
            }));
            
            setShowGalleryBrowser(false);
            toast.success(`Thumbnail "${selectedMedia.title || selectedMedia.name}" selected!`);
        } else {
            // Selected for main media URL
            // Detect media type from file extension if not already set correctly
            const extension = mediaUrl.toLowerCase().split('.').pop();
            let detectedType = formData.mediaType;
            
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
                detectedType = 'Image';
            } else if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(extension)) {
                detectedType = 'Video';
            } else if (['mp3', 'wav', 'm4a', 'flac'].includes(extension)) {
                detectedType = 'Audio';
            }
            
            setFormData(prev => ({ 
                ...prev, 
                webURLLink: mediaUrl,
                mediaType: detectedType
            }));
            
            setShowGalleryBrowser(false);
            toast.success(`${detectedType} "${selectedMedia.title || selectedMedia.name}" selected!`);
        }
    };

    const handleOpenGalleryBrowser = () => {
        if (galleries.length === 0) {
            loadGalleries();
        }
        setShowGalleryBrowser(true);
    };

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            if (isEditMode && editingItem) {
               
                
                // Show all description-related fields
                const descriptionFields = Object.keys(editingItem).filter(key => 
                    key.toLowerCase().includes('description') || 
                    key.toLowerCase().includes('desc') ||
                    key.toLowerCase().includes('summary') ||
                    key.toLowerCase().includes('content')
                );
               
                descriptionFields.forEach(field => {
                   
                });
                
                // Show all URL-related fields  
                const urlFields = Object.keys(editingItem).filter(key => 
                    key.toLowerCase().includes('url') || 
                    key.toLowerCase().includes('link') ||
                    key.toLowerCase().includes('media')
                );
               
                urlFields.forEach(field => {
                   
                });
                
                // Helper function to get non-empty value from multiple field names
                const getNonEmptyValue = (item, fieldNames, defaultValue = '') => {
                    for (const fieldName of fieldNames) {
                        const value = item[fieldName];
                        if (value && value.trim && value.trim() !== '') {
                            return value;
                        } else if (value && typeof value !== 'string') {
                            return value;
                        }
                    }
                    return defaultValue;
                };
                
                // Populate form with existing item data using exact field names from your data
                const formDataToSet = {
                    webURLLink: getNonEmptyValue(editingItem, ['MediaURL', 'mediaUrl', 'MediaUrl', 'webURLLink', 'WebURLLink', 'url', 'URL']),
                    mediaType: editingItem.mediaType || editingItem.MediaType || 'Video',
                    topicName: editingItem.topic || editingItem.Topic || editingItem.topicName || editingItem.TopicName || editingItem.title || editingItem.Title || '',
                    shortDescription: getNonEmptyValue(editingItem, ['ShortDescriptionOfContent', 'shortDescription', 'ShortDescription', 'description', 'Description', 'summary', 'Summary', 'content', 'Content', 'desc', 'Desc']),
                    series: Array.isArray(editingItem.series || editingItem.Series) 
                        ? (editingItem.series || editingItem.Series)
                        : (editingItem.series || editingItem.Series) ? [editingItem.series || editingItem.Series] : [],
                    productService: editingItem.productService || editingItem.ProductService || editingItem.product || editingItem.Product || '',
                    theme: editingItem.theme || editingItem.Theme || '',
                    entity: editingItem.entity || editingItem.Entity || 'IF',
                    thumbnail: editingItem.thumbnail || editingItem.Thumbnail || editingItem.thumbnailUrl || editingItem.ThumbnailUrl || '',
                    briefingCode: editingItem.briefingCode || editingItem.BriefingCode || '',
                    published: editingItem.published !== undefined ? editingItem.published : (editingItem.Published !== undefined ? editingItem.Published : true),
                    publishDate: editingItem.publishDate || editingItem.PublishDate 
                        ? new Date(editingItem.publishDate || editingItem.PublishDate).toISOString().split('T')[0]
                        : new Date().toISOString().split('T')[0]
                };
                
               
                setFormData(formDataToSet);
                
               
            } else {
                // Reset form for new item
                setFormData({
                    webURLLink: '',
                    mediaType: 'Video',
                    topicName: '',
                    shortDescription: '',
                    series: [],
                    productService: '',
                    theme: '',
                    entity: 'IF',
                    thumbnail: '',
                    briefingCode: '',
                    published: true,
                    publishDate: new Date().toISOString().split('T')[0] // Default to today
                });
            }
            setUrlPreview(null);
            setShowGalleryBrowser(false);
            setGalleryBrowseTarget('media');
            setIsAddingNewTopic(false);
            setCustomTopicName('');
            setIsAddingNewProduct(false);
            setCustomProductName('');
            setIsAddingNewTheme(false);
            setCustomThemeName('');
            setIsAddingNewEntity(false);
            setCustomEntityName('');
        }
    }, [isOpen, isEditMode, editingItem]);

    // Auto-detect media type from URL.
    // Skipped for Briefing: it has no URL, so a stale MediaURL left on the record
    // must not silently flip the type back to Video when the item is reopened.
    useEffect(() => {
        if (isBriefing) {
            setUrlPreview(null);
            return;
        }
        if (formData.webURLLink) {
            const url = formData.webURLLink.toLowerCase();
            let detectedType = 'Video';
            let preview = null;

            if (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com')) {
                detectedType = 'Video';
                preview = { platform: 'Video Platform', icon: faVideo };
            } else if (url.includes('spotify.com') || url.includes('soundcloud.com') || 
                      url.includes('anchor.fm') || url.includes('pod.link') ||
                      url.includes('.mp3') || url.includes('.wav') || url.includes('.m4a')) {
                detectedType = 'Audio';
                preview = { platform: 'Audio Platform', icon: faMicrophone };
            } else if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || 
                      url.includes('.gif') || url.includes('.webp')) {
                detectedType = 'Image';
                preview = { platform: 'Image URL', icon: faImage };
            } else if (url.startsWith('http')) {
                preview = { platform: 'External URL', icon: faExternalLinkAlt };
            }

            setFormData(prev => ({ ...prev, mediaType: detectedType }));
            setUrlPreview(preview);
        } else {
            setUrlPreview(null);
        }
    }, [formData.webURLLink, isBriefing]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Clear URL when media type changes for better UX
        if (name === 'mediaType' && value !== formData.mediaType) {
            const hadUrl = formData.webURLLink.trim() !== '';
            
            setFormData(prev => ({
                ...prev,
                [name]: value,
                webURLLink: '', // Clear URL when changing media type
                // Drop the briefing code when leaving Briefing — it is meaningless
                // on any other type and would otherwise be written back on save.
                briefingCode: value === 'Briefing' ? prev.briefingCode : ''
            }));
            setUrlPreview(null); // Clear preview too

            // Show helpful toast if there was a URL that got cleared. Briefing has
            // no URL field, so the message would be misleading there.
            if (hadUrl && value !== 'Briefing') {
                toast.info(`Media type changed to ${value} - URL field cleared for new input`, {
                    position: "top-center",
                    autoClose: 3000
                });
            }
            return;
        }
        
        // Handle dropdown changes for fill-in choice fields
        if (name === 'topicName') {
            if (value === 'ADD_NEW_TOPIC') {
                setIsAddingNewTopic(true);
                setFormData(prev => ({ ...prev, [name]: customTopicName }));
            } else {
                setIsAddingNewTopic(false);
                setCustomTopicName('');
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else if (name === 'productService') {
            if (value === 'ADD_NEW_PRODUCT') {
                setIsAddingNewProduct(true);
                setFormData(prev => ({ ...prev, [name]: customProductName }));
            } else {
                setIsAddingNewProduct(false);
                setCustomProductName('');
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else if (name === 'theme') {
            if (value === 'ADD_NEW_THEME') {
                setIsAddingNewTheme(true);
                setFormData(prev => ({ ...prev, [name]: customThemeName }));
            } else {
                setIsAddingNewTheme(false);
                setCustomThemeName('');
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else if (name === 'entity') {
            if (value === 'ADD_NEW_ENTITY') {
                setIsAddingNewEntity(true);
                setFormData(prev => ({ ...prev, [name]: customEntityName }));
            } else {
                setIsAddingNewEntity(false);
                setCustomEntityName('');
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            // Handle checkbox inputs (like published toggle)
            if (type === 'checkbox') {
                setFormData(prev => ({ ...prev, [name]: checked }));
            } else {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        }
    };

    const handleSeriesChange = (seriesName, isChecked) => {
        setFormData(prev => {
            if (isChecked) {
                return { ...prev, series: [...prev.series, seriesName] };
            } else {
                return { ...prev, series: prev.series.filter(s => s !== seriesName) };
            }
        });
    };

    const handleCustomTopicChange = (e) => {
        const value = e.target.value;
        setCustomTopicName(value);
        setFormData(prev => ({ ...prev, topicName: value }));
    };

    const handleCustomProductChange = (e) => {
        const value = e.target.value;
        setCustomProductName(value);
        setFormData(prev => ({ ...prev, productService: value }));
    };

    const handleCustomThemeChange = (e) => {
        const value = e.target.value;
        setCustomThemeName(value);
        setFormData(prev => ({ ...prev, theme: value }));
    };

    const handleCustomEntityChange = (e) => {
        const value = e.target.value;
        setCustomEntityName(value);
        setFormData(prev => ({ ...prev, entity: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Validate required fields. A Briefing is identified by its briefing
            // code and has no media URL, so the two swap places here.
            if (!formData.topicName || !formData.shortDescription ||
                !formData.productService || !formData.theme || formData.series.length === 0) {
                toast.error('Please fill in all required fields');
                setIsSubmitting(false);
                return;
            }

            if (isBriefing) {
                if (!formData.briefingCode.trim()) {
                    toast.error('Please enter a briefing code');
                    setIsSubmitting(false);
                    return;
                }
            } else if (!formData.webURLLink.trim()) {
                // Just ensure webURLLink has a value (could be URL or filename)
                toast.error('Please enter a media URL or select from gallery');
                setIsSubmitting(false);
                return;
            }

            if (isEditMode && editingItem) {
               
                
                // Update existing item
                await updateMediaCentreItem(editingItem.id || editingItem.Id, {
                    ...formData,
                    originalName: editingItem.originalName || `External_${formData.mediaType}_${Date.now()}`,
                    featured: editingItem.featured || false
                });

                toast.success(`${formData.mediaType} "${formData.topicName}" updated successfully!`);
            } else {
                

                // Send to Media Centre
                await sendToMediaCentre({
                    ...formData,
                    originalName: `External_${formData.mediaType}_${Date.now()}`,
                    featured: false
                });

                toast.success(`${formData.mediaType} "${formData.topicName}" added to Media Centre successfully!`);
            }

            onSuccess();

        } catch (error) {
            console.error('Error adding media to Media Centre:', error);
            toast.error('Failed to add media to Media Centre: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getMediaTypeIcon = (type) => {
        switch (type) {
            case 'Video': return faVideo;
            case 'Audio': return faMicrophone;
            case 'Image': return faImage;
            default: return faVideo;
        }
    };

    return (
        <Modal show={isOpen} onHide={onClose} size="lg" centered className="modal-add-media-centre">
            <Modal.Header>
                <Modal.Title>
                    <FontAwesomeIcon icon={isEditMode ? faEdit : faPlus} className="me-2" />
                    {isEditMode ? 'Edit Media Centre Item' : 'Add External Media to Media Centre'}
                </Modal.Title>
                <Button variant="link" onClick={onClose} className="p-0 border-0">
                    <FontAwesomeIcon icon={faTimes} />
                </Button>
            </Modal.Header>

            <Modal.Body>
                <Alert variant="info" className="mb-4">
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        <div>
                            <strong>{isEditMode ? 'Edit Content:' : 'Add Content:'}</strong> {isEditMode ? 'Update the details for this Media Centre item' : 'Add videos from Vimeo/YouTube, audio from Spotify/SoundCloud, or any external media URL to the Media Centre'}
                        </div>
                    </div>
                </Alert>

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Media Type <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="mediaType"
                                    value={formData.mediaType}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="Video">Video</option>
                                    <option value="Audio">Audio</option>
                                    <option value="Image">Image</option>
                                    <option value="Briefing">Briefing</option>
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Choose the type of media you want to add
                                </Form.Text>
                            </Form.Group>
                        </Col>
                         
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Publish Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="publishDate"
                                    value={formData.publishDate}
                                    onChange={handleInputChange}
                                    max={new Date().toISOString().split('T')[0]}
                                    required
                                />
                                <Form.Text className="text-muted">
                                    When this content was originally published (cannot be a future date)
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Briefing: the Wolfox widget renders the player, so a briefing
                        code replaces the media URL and thumbnail entirely. */}
                    {isBriefing && (
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Briefing Code <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="briefingCode"
                                        value={formData.briefingCode}
                                        onChange={handleInputChange}
                                        placeholder='e.g. "latest" or a specific briefing code'
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        The audio briefing to play. Use "latest" for the most recent briefing, or a specific briefing code.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    {/* Hide thumbnail field for Image media type since the image itself is the thumbnail */}
                    {formData.mediaType !== 'Image' && !isBriefing && (
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Thumbnail Image</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            name="thumbnail"
                                            value={formData.thumbnail}
                                            onChange={handleInputChange}
                                            placeholder="Enter thumbnail URL or browse gallery for image..."
                                        />
                                        <Button 
                                            variant="outline-primary"
                                            onClick={() => {
                                                setGalleryBrowseTarget('thumbnail');
                                                if (galleries.length === 0) {
                                                    loadGalleries();
                                                }
                                                setShowGalleryBrowser(true);
                                            }}
                                            disabled={loadingGalleries}
                                            title="Browse gallery for thumbnail image"
                                        >
                                            {loadingGalleries ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : (
                                                <FontAwesomeIcon icon={faFolderOpen} />
                                            )}
                                        </Button>
                                    </div>
                                    <Form.Text className="text-muted">
                                        Optional - Choose a thumbnail image from the gallery or enter an external image URL
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    {!isBriefing && (
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Media URL or Gallery Item<span className="text-danger">*</span></Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Control
                                        type="url"
                                        name="webURLLink"
                                        value={formData.webURLLink}
                                        onChange={handleInputChange}
                                        placeholder="Enter URL or browse gallery for any media type..."
                                        required
                                    />
                                    <Button 
                                        variant="outline-primary"
                                        onClick={() => {
                                            setGalleryBrowseTarget('media');
                                            handleOpenGalleryBrowser();
                                        }}
                                        disabled={loadingGalleries}
                                        title="Browse gallery for any media type"
                                    >
                                        {loadingGalleries ? (
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                        ) : (
                                            <FontAwesomeIcon icon={faFolderOpen} />
                                        )}
                                    </Button>
                                </div>
                                {/* {urlPreview && (
                                    <div className="mt-2 p-2 bg-light rounded d-flex align-items-center">
                                        <FontAwesomeIcon icon={urlPreview.icon} className="me-2 text-success" />
                                        <span className="small text-muted">Detected: {urlPreview.platform}</span>
                                    </div>
                                )} */}
                                <Form.Text className="text-muted">
                                    Enter external URLs (Vimeo, YouTube, Spotify, SoundCloud) or browse the gallery to select uploaded media
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    )}

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Topic Name <span className="text-danger">*</span></Form.Label>
                                {isAddingNewTopic ? (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customTopicName}
                                            onChange={handleCustomTopicChange}
                                            placeholder="Enter new topic name..."
                                            required
                                            autoFocus
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => {
                                                setIsAddingNewTopic(false);
                                                setCustomTopicName('');
                                                setFormData(prev => ({ ...prev, topicName: '' }));
                                            }}
                                        >
                                            ↩️ Back
                                        </Button>
                                    </div>
                                ) : (
                                    <Form.Select
                                        name="topicName"
                                        value={formData.topicName}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Topic Name</option>
                                        {choiceFields?.topicNames?.map(topic => (
                                            <option key={topic} value={topic}>{topic}</option>
                                        ))}
                                        <option value="ADD_NEW_TOPIC" style={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                            ➕ Add New Topic...
                                        </option>
                                    </Form.Select>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Short Description <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="shortDescription"
                                    value={formData.shortDescription}
                                    onChange={handleInputChange}
                                    placeholder="Brief description of the content"
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Series <span className="text-danger">*</span></Form.Label>
                                <div className="series-selection border rounded p-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                    {choiceFields?.series?.length > 0 ? (
                                        choiceFields.series.map(series => (
                                            <Form.Check
                                                key={series}
                                                type="checkbox"
                                                id={`series-${series}`}
                                                label={series}
                                                checked={formData.series.includes(series)}
                                                onChange={(e) => handleSeriesChange(series, e.target.checked)}
                                                className="mb-1"
                                            />
                                        ))
                                    ) : (
                                        <div className="text-muted">No series found</div>
                                    )}
                                </div>
                                <Form.Text className="text-muted">
                                    {formData.series.length > 0 
                                        ? `Selected: ${formData.series.join(', ')}` 
                                        : 'Select one or more series (required)'
                                    }
                                </Form.Text>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Product/Service <span className="text-danger">*</span></Form.Label>
                                {!isAddingNewProduct ? (
                                    <Form.Select
                                        name="productService"
                                        value={formData.productService}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Product/Service</option>
                                        {choiceFields?.products?.map(product => (
                                            <option key={product} value={product}>{product}</option>
                                        ))}
                                        <option value="ADD_NEW_PRODUCT" style={{borderTop: '1px solid #ccc', fontStyle: 'italic'}}>
                                            ➕ Add New Product/Service...
                                        </option>
                                    </Form.Select>
                                ) : (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customProductName}
                                            onChange={handleCustomProductChange}
                                            placeholder="Enter new product/service name"
                                            autoFocus
                                            required
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                setIsAddingNewProduct(false);
                                                setCustomProductName('');
                                                setFormData(prev => ({ ...prev, productService: '' }));
                                            }}
                                        >
                                            ↩️
                                        </Button>
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Theme <span className="text-danger">*</span></Form.Label>
                                {!isAddingNewTheme ? (
                                    <Form.Select
                                        name="theme"
                                        value={formData.theme}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Theme</option>
                                        {choiceFields?.themes?.map(theme => (
                                            <option key={theme} value={theme}>{theme}</option>
                                        ))}
                                        <option value="ADD_NEW_THEME" style={{borderTop: '1px solid #ccc', fontStyle: 'italic'}}>
                                            ➕ Add New Theme...
                                        </option>
                                    </Form.Select>
                                ) : (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customThemeName}
                                            onChange={handleCustomThemeChange}
                                            placeholder="Enter new theme name"
                                            autoFocus
                                            required
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                setIsAddingNewTheme(false);
                                                setCustomThemeName('');
                                                setFormData(prev => ({ ...prev, theme: '' }));
                                            }}
                                        >
                                            ↩️
                                        </Button>
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Entity</Form.Label>
                                {!isAddingNewEntity ? (
                                    <Form.Select
                                        name="entity"
                                        value={formData.entity}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Entity</option>
                                        {choiceFields?.entities?.map(entity => (
                                            <option key={entity} value={entity}>{entity}</option>
                                        ))}
                                        <option value="ADD_NEW_ENTITY" style={{borderTop: '1px solid #ccc', fontStyle: 'italic'}}>
                                            ➕ Add New Entity...
                                        </option>
                                    </Form.Select>
                                ) : (
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={customEntityName}
                                            onChange={handleCustomEntityChange}
                                            placeholder="Enter new entity name"
                                            autoFocus
                                            required
                                        />
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => {
                                                setIsAddingNewEntity(false);
                                                setCustomEntityName('');
                                                setFormData(prev => ({ ...prev, entity: '' }));
                                            }}
                                        >
                                            ↩️
                                        </Button>
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Published Status</Form.Label>
                                <div className="d-flex align-items-center gap-3 mt-2">
                                    <Form.Check
                                        type="switch"
                                        id="published-switch"
                                        name="published"
                                        checked={formData.published}
                                        onChange={handleInputChange}
                                        label=""
                                        className="published-switch"
                                    />
                                    <span className={`published-status ${formData.published ? 'published' : 'unpublished'}`}>
                                        {formData.published ? (
                                            <>
                                                <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                                                Published
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faTimesCircle} className="me-2 text-danger" />
                                                Unpublished
                                            </>
                                        )}
                                    </span>
                                </div>
                                <Form.Text className="text-muted">
                                    Toggle to publish/unpublish this item
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                </Form>
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                {isEditMode ? 'Updating...' : 'Adding...'}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={isEditMode ? faEdit : faPlus} className="me-2" />
                                {isEditMode ? 'Update Item' : 'Add to Media Centre'}
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Footer>

            {/* Gallery Browser Modal */}
            {showGalleryBrowser && (
                <GalleryBrowser
                    galleries={galleries}
                    mediaType={galleryBrowseTarget === 'thumbnail' 
                        ? 'image' 
                        : (formData.mediaType.toLowerCase() === 'audio' ? 'podcast' : formData.mediaType.toLowerCase())
                    }
                    onSelect={handleMediaSelect}
                    onClose={() => setShowGalleryBrowser(false)}
                    onRefreshGalleries={loadGalleries}
                />
            )}
        </Modal>
    );
};

export default ModalAddMediaCentre; 